import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Contains a render error so one bad screen cannot white-out the whole app.
 *
 * There is no router here, so an uncaught throw unmounts the React root and
 * leaves a blank page — exactly what the Logs toLowerCase crash used to do.
 * Wrapped around the active tab (keyed by tab, so navigating away clears it)
 * and around the whole app as a backstop.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('UI error boundary caught:', error, info);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="flex min-h-[60vh] w-full items-center justify-center p-4">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-2xl border border-rose-200 bg-white p-8 text-center shadow-sm dark:border-rose-500/30 dark:bg-navy-800">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-600 dark:text-rose-400 dark:bg-rose-500/15">
            <AlertTriangle size={24} />
          </div>
          <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">Mục này gặp lỗi hiển thị</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Bạn có thể chuyển sang mục khác, hoặc tải lại trang.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-1 flex items-center gap-2 rounded-xl bg-navy-900 px-4 min-h-11 py-2 text-sm font-bold text-white transition-colors hover:bg-navy-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:focus-visible:ring-brand-400 dark:bg-brand-700 dark:hover:bg-brand-700"
          >
            <RefreshCw size={16} /> Tải lại trang
          </button>
        </div>
      </div>
    );
  }
}
